import { z } from "zod";
import { browserAgentFindingDataSchema, browserAgentFindingOutcomeSchema, } from "./browser-agent.js";
import { ufSchema } from "./calculation.js";
/**
 * Tax lookup jobs: a backend workflow (Step Functions) submits thousands of
 * NCM × route entries at once and polls until every entry is answered.
 *
 * - Entries a published rule already covers are answered at submit time
 *   (COVERED). That is the cache path and it is fast.
 * - The rest go to research: the IOB browser agent queue and, when asked, web
 *   research (Vertex Gemini grounded search) for what IOB cannot answer.
 * - FOUND / NO_ST are research findings, i.e. review material. They never
 *   calculate on their own; a reviewer turns them into published rules, after
 *   which the same entry is COVERED for every later job.
 */
const ncmSchema = z.string().regex(/^\d{8}$/);
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
export const TAX_LOOKUP_JOB_MAX_ENTRIES = 10_000;
export const taxLookupEntryInputSchema = z
    .object({
    ncm: ncmSchema,
    cest: cestSchema.nullable().default(null),
    originUf: ufSchema,
    destinationUf: ufSchema,
    /** Caller's own reference (SKU, lot row); echoed back, never interpreted. */
    ref: z.string().trim().min(1).max(100).nullable().default(null),
})
    .strict();
export const taxLookupJobCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    workflowId: z.string().trim().min(1).max(100),
    /** Rules are matched as of this date. */
    calculationDate: z.string().date(),
    entries: z.array(taxLookupEntryInputSchema).min(1).max(TAX_LOOKUP_JOB_MAX_ENTRIES),
    research: z
        .object({
        /** Queue uncovered entries for the IOB browser agent. */
        iob: z.boolean().default(true),
        /** Web research (Vertex grounded search) for entries IOB cannot answer. */
        web: z.boolean().default(false),
    })
        .strict()
        .default({ iob: true, web: false }),
})
    .strict()
    .superRefine((job, context) => {
    const seen = new Set();
    job.entries.forEach((entry, index) => {
        const key = `${entry.ncm}|${entry.cest ?? ""}|${entry.originUf}|${entry.destinationUf}`;
        if (seen.has(key)) {
            context.addIssue({
                code: "custom",
                path: ["entries", index],
                message: "Duplicate NCM + CEST + route in the same job",
            });
        }
        seen.add(key);
    });
});
export const taxLookupEntryStatusSchema = z.enum([
    /** A published rule answers it (cache hit). Terminal. */
    "COVERED",
    /** Waiting in the research queue. */
    "QUEUED",
    /** A research lane is working on it. */
    "RESEARCHING",
    /** Research found ICMS-ST data; pending review before it calculates. Terminal. */
    "FOUND",
    /** Research confirmed there is no ST for the route; pending review. Terminal. */
    "NO_ST",
    /** Neither lane could find the NCM or read an answer. Terminal. */
    "NOT_FOUND",
    /** Research gave up (retry budget, lane disabled). Terminal. */
    "FAILED",
]);
export const TAX_LOOKUP_TERMINAL_STATUSES = [
    "COVERED",
    "FOUND",
    "NO_ST",
    "NOT_FOUND",
    "FAILED",
];
export const taxLookupJobStatusSchema = z.enum(["RUNNING", "COMPLETED", "CANCELLED"]);
export const taxLookupJobCountsSchema = z
    .object(Object.fromEntries(taxLookupEntryStatusSchema.options.map((status) => [status, z.number().int().nonnegative()])))
    .strict();
export const taxLookupJobSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    workflowId: z.string(),
    calculationDate: z.string().date(),
    status: taxLookupJobStatusSchema,
    research: z.object({ iob: z.boolean(), web: z.boolean() }).strict(),
    entryCount: z.number().int().positive(),
    counts: taxLookupJobCountsSchema,
    /** Browser agent task that holds this job's IOB lookups, when any. */
    taskId: z.string().uuid().nullable(),
    /** Rough ETA from the agent's pace and daily window; null when unknown. */
    estimatedCompletionAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
})
    .strict();
export const taxLookupEntryResultSchema = z
    .object({
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    originUf: ufSchema,
    destinationUf: ufSchema,
    ref: z.string().nullable(),
    status: taxLookupEntryStatusSchema,
    /** Present when COVERED. */
    rule: z.object({ fiscalRuleId: z.string(), rulesetVersion: z.string() }).strict().nullable(),
    /** Present when a research finding answered it (FOUND / NO_ST / NOT_FOUND). */
    finding: z
        .object({
        id: z.string().uuid(),
        outcome: browserAgentFindingOutcomeSchema,
        data: browserAgentFindingDataSchema,
    })
        .strict()
        .nullable(),
    /** Web research task, when that lane was used. */
    webResearchTaskId: z.string().uuid().nullable(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const taxLookupEntryPageSchema = z
    .object({
    items: z.array(taxLookupEntryResultSchema).max(1_000),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
})
    .strict();
export const taxLookupJobPageSchema = z
    .object({
    items: z.array(taxLookupJobSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=lookup-jobs.js.map